import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Button, ListItemIcon } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons';
import { TypographySettings } from '../config';
import { DynamicLink, GetPostRequestParametersResponse } from '../../api-service/clients';
import { WindowService } from '../window-service/window-service';
import { useDynamicLinks } from '../../hooks/useDynamicLinks';
import { AbsolutelyCentered } from '../absolutely-centered';
import { CircularProgress } from '../circular-progress';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import {
  GenericPostRequestPageForm,
  GenericPostRequestPageFormComponent,
  PostRequestTypeToComponent,
} from '../../areas/citizen/authority/details/components/jump-to/generic-post-request-page-form';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 400,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 6,
  p: 0,
  outline: 'none',
  minHeight: 100,
};

const dynamicLinkClient = moduleClientFactory.createDynamicLinkClient(true);

interface PostRequestFormState {
  componentName: GenericPostRequestPageFormComponent;
  postRequestData: GetPostRequestParametersResponse;
  linkUrl: string;
  autoSubmit: boolean;
}

interface DynamicLinksProps {
  resourceType: string;
  resourceId?: string;
  citizenId?: string;
  onClose: () => void;
}

export function DynamicLinks({ resourceType, resourceId, citizenId, onClose }: DynamicLinksProps) {
  const [postRequestForm, setPostRequestForm] = React.useState<PostRequestFormState>();
  const [postRequestData, setPostRequestData] = React.useState<GetPostRequestParametersResponse>();
  const { dynamicLinks, loading } = useDynamicLinks(resourceType, resourceId ?? '');
  const enabledLinks = dynamicLinks?.filter(link => link.disabled === false);

  React.useEffect(() => {
    async function fetchPostRequestData() {
      if (!enabledLinks?.length || postRequestData || !citizenId) {
        return;
      }

      const postRequestTypes = enabledLinks.flatMap((link: DynamicLink) => (link.postRequestType ? [link.postRequestType] : []));
      if (!postRequestTypes.length) {
        return;
      }

      const result = await dynamicLinkClient.getPostRequestParameters({ citizenId, postRequestTypes });
      setPostRequestData(result);
    }
    fetchPostRequestData();
  }, [dynamicLinks]);

  function openLink(link: DynamicLink) {
    if (link.postRequestType && postRequestData && link.url) {
      setPostRequestForm({
        componentName: PostRequestTypeToComponent(link.postRequestType),
        postRequestData,
        linkUrl: link.url,
        autoSubmit: link.autoSubmitPostRequest ?? true,
      });
      return;
    }

    if (link.url) {
      WindowService.open(link.url);
    }
    onClose();
  }

  return (
    <Modal open onClose={onClose}>
      <Paper sx={modalStyle}>
        {loading ? (
          <AbsolutelyCentered>
            <CircularProgress />
          </AbsolutelyCentered>
        ) : (
          <>
            <Typography component="div" sx={{ fontFamily: TypographySettings.AlternativeFontFamily, fontSize: '1rem', fontWeight: 500, color: '#333', p: 2 }}>
              Hop til
            </Typography>
            <Divider />
            <Box sx={{ maxHeight: 'calc(100vh - 90px - 16px)', overflowY: 'auto' }}>
              {enabledLinks?.length ? (
                <MenuList>
                  {enabledLinks
                    .filter(link => !link.postRequestType || postRequestData)
                    .map((link: DynamicLink) => (
                      <MenuItem onClick={() => openLink(link)} key={link.text} sx={{ display: 'flex' }}>
                        <ListItemIcon>
                          <FontAwesomeIcon icon={faLink} />
                        </ListItemIcon>
                        <ListItemText slotProps={{ primary: { sx: { fontSize: 14 } }, secondary: { sx: { fontSize: 12 } } }} secondary={link.tooltip}>
                          {link.text}
                        </ListItemText>
                      </MenuItem>
                    ))}
                </MenuList>
              ) : (
                <Typography component="div" sx={{ fontFamily: TypographySettings.DefaultFontFamily, fontSize: '1rem', fontWeight: 400, color: '#333', p: 2 }}>
                  Der er ikke konfigureret links for den valgte entitet.
                </Typography>
              )}
            </Box>
            {postRequestForm && (
              <GenericPostRequestPageForm
                componentName={postRequestForm.componentName}
                getPostRequestParametersResponse={postRequestForm.postRequestData}
                linkurl={postRequestForm.linkUrl}
                onCompleted={() => setPostRequestForm(undefined)}
                autoSubmit={postRequestForm.autoSubmit}
              />
            )}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'end', p: 2 }}>
              <Button onClick={onClose} color="primary" variant="text">
                Luk
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Modal>
  );
}
